import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { PurchaseRequisitionPreview } from '../types/IPurchaseRequisitionPreview';
import {
    CustomRouter,
    IPaginationSortingResponse,
    NotificationService,
    SortDirection,
    Sorting,
    StorageService,
    View,
} from '@lib/core';
import { PurchaseRequisitionDataService } from '../purchase-requisition-data.service';
import {
    PurchaseRequisitionListFilter,
    PurchaseRequisitionListFilterKey,
} from '../types/PurchaseRequisitionListFilter';
import { ActivatedRoute, Params } from '@angular/router';
import { FormControl } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { finalize, firstValueFrom, Observable, of, Subject } from 'rxjs';
import {
    DialogOptions,
    OrderByPipe,
    PurchaseRequisitionDeliveryStatus,
    PurchaseRequisitionOrderStatus,
    SubscriptionHelper,
} from '@lib/util';
import { catchError, debounceTime, takeUntil } from 'rxjs/operators';
import { SupplierCompany, SupplierDataService } from '@lib/supplier';
import { PurchaseRequisitionStatusEnum } from '../types/PurchaseRequistionStatusEnum';
import { PurchaseRequisitionSorting } from '../types/PurchaseRequisitionSorting';
import { I18nService, PaginationTranslation } from '@lib/i18n';
import { PurchaseRequisitionPagination } from '../types/PurchaseRequisitionPagination';
import { EprocUser, PersonExtended, UserPaginationService, UserService } from '@lib/user';
import { DemandDataService } from '../../demand/demand-data.service';
import { AclAction, AclResource, AclService } from '@lib/acl';
import { BC, BreadcrumbService } from '@lib/shared/breadcrumbs';
import { CustomerDepartment } from '@lib/client';
import { selectableConsumerToPerson } from '../types/ISelectableConsumer';
import { EprocFeature } from '@lib/feature-toggle';
import { MatDialog } from '@angular/material/dialog';
import {
    PurchaseRequisitionPdfUploadAmbiguousResult,
    PurchaseRequisitionPdfUploadDialogComponent,
    PurchaseRequisitionPdfUploadDialogResult,
} from './dialog/purchase-requisition-pdf-upload-dialog.component';
import {
    PdfSupplierSelectionDialogComponent,
    PdfSupplierSelectionDialogData,
    PdfSupplierSelectionDialogResult,
} from './dialog/pdf-supplier-selection-dialog/pdf-supplier-selection-dialog.component';

@Component({
    selector: 'purchase-requisition-list',
    templateUrl: './purchase-requisition-list.component.html',
    styleUrls: ['./purchase-requisition-list.component.scss'],
    standalone: false,
})
export class PurchaseRequisitionListComponent extends SubscriptionHelper implements OnInit {
    readonly router = inject(CustomRouter);
    private readonly route = inject(ActivatedRoute);
    private readonly storage = inject(StorageService);
    private readonly purchaseRequisitionDataService = inject(PurchaseRequisitionDataService);
    private readonly supplierDataService = inject(SupplierDataService);
    private readonly paginationService = inject(UserPaginationService);
    private readonly i18n = inject(I18nService);
    private readonly demandDataService = inject(DemandDataService);
    private readonly notificationService = inject(NotificationService);
    private readonly aclService = inject(AclService);
    private readonly breadcrumbService = inject(BreadcrumbService);
    private readonly userService = inject(UserService);
    private readonly dialog = inject(MatDialog);
    private readonly bottomSheet = inject(MatBottomSheet);

    public purchaseRequisitions?: PurchaseRequisitionPreview[];
    public filter?: PurchaseRequisitionListFilter;
    public suppliers?: SupplierCompany[];

    public View = View;
    public FilterKey = PurchaseRequisitionListFilterKey;
    public Status = PurchaseRequisitionStatusEnum;
    public OrderStatus = PurchaseRequisitionOrderStatus;
    public DeliveryStatus = PurchaseRequisitionDeliveryStatus;
    public EprocFeature = EprocFeature;

    public sorting: Sorting<PurchaseRequisitionSorting> = new Sorting<PurchaseRequisitionSorting>(
        PurchaseRequisitionSorting.CREATED,
        SortDirection.DESC,
    );
    public pagination!: PurchaseRequisitionPagination;
    public paginationTranslations: PaginationTranslation = new PaginationTranslation();

    public filterChanged$: Subject<void> = new Subject<void>();

    /**
     * Search field in the list header. It writes into the same `filter.displayNumber` as the "Nummer"
     * field of the filter panel, and because a `FormControl` is not kept in sync by Angular the way two
     * `ngModel` bindings would be, `syncNumberSearchControl()` does it explicitly (EPROC-11936).
     */
    public readonly numberSearchControl = new FormControl<string>('', { nonNullable: true });

    @ViewChild('numberSearchInput')
    private readonly numberSearchInput?: ElementRef<HTMLInputElement>;

    public unannouncedDemandExists = false;

    public orderLinkAllowed = false;
    public rfqLinkAllowed = false;
    public clients?: PersonExtended[];
    public departments?: CustomerDepartment[];
    private user!: EprocUser;
    private supplierOptionsLoaded?: Promise<void>;
    private destroyed = false;

    public override ngOnDestroy(): void {
        this.destroyed = true;
        super.ngOnDestroy();
    }

    async ngOnInit(): Promise<void> {
        this.user = await this.userService.getMyself();
        this.breadcrumbService.set(BC.PURCHASE_REQUISITION.LIST);
        await this.getFilter();
        this.orderLinkAllowed = await this.aclService.getPermissionSync(AclResource.orderMyShop, AclAction.SHOW);
        this.rfqLinkAllowed = await this.aclService.getPermissionSync(AclResource.rfqMyShop, AclAction.SHOW);
        this.getData();
        void this.getSupplierOptions();
        this.doesUnannouncedDemandExist();
        this.demandDataService.unannouncedDemandChanged$
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(() => this.doesUnannouncedDemandExist());
        this.filterChanged$.pipe(debounceTime(300), takeUntil(this.onDestroy$)).subscribe(async () => {
            this.pagination = await PurchaseRequisitionPagination.default(this.storage, this.paginationService);
            this.syncNumberSearchControl();
            this.applyFilterToUrl();
            this.getData();
        });
        this.registerNumberSearchListener();
    }

    /**
     * Typing in the header search field feeds the same subject the filter panel uses, so the search
     * keeps its single 300 ms debounce and its reset to page one instead of adding a second one.
     *
     * <p>Compared against the applied filter rather than guarded by `distinctUntilChanged()`: every
     * programmatic write to the control passes `emitEvent: false` and would never reach that operator,
     * so its remembered value would go stale and swallow a search for a number that was searched for
     * once and then cleared.</p>
     */
    private registerNumberSearchListener(): void {
        this.numberSearchControl.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((value: string) => {
            if (this.filter && this.filter.displayNumber !== value.trim()) {
                this.filter.displayNumber = value;
                this.filterChanged$.next();
            }
        });
    }

    /**
     * Enter does what the arrow button at the bottom of the filter panel does (EPROC-11936): it takes
     * the typed value over and closes the panel, which is what starts the search - dismissing emits
     * `filterClosed` and `onSort`, and the 300 ms debounce collapses the two into one request.
     *
     * <p>The value comes from the filter component's `value` property rather than from its
     * `valueChange` output, because that output is debounced by 300 ms and cancelled on destroy: a
     * value typed right before the panel closes would never reach the filter.</p>
     *
     * <p>Deliberately no `filterChanged$.next()` of its own. `afterDismissed()` only fires once the
     * 375 ms closing animation has run, so an extra trigger would not be collapsed by the debounce -
     * it would search a second time, and with the sorting state from before the panel closed.</p>
     */
    public submitFilterPanelSearch(
        field: PurchaseRequisitionListFilterKey.DISPLAY_NUMBER | PurchaseRequisitionListFilterKey.CONTENT,
        rawValue: string,
    ): void {
        if (this.filter) {
            this.filter[field] = rawValue;
        }
        this.bottomSheet.dismiss();
    }

    /**
     * Empties the number search, from the trash icon of the header field as well as from the filter
     * chip above the list. The control is cleared directly - `syncNumberSearchControl()` would do it
     * too, but only after the 300 ms debounce, and until then the field would still show the number it
     * no longer filters by.
     *
     * <p>`returnFocus` is set only by the trash icon: the `@if` around it removes the button the click
     * came from, which would drop the focus to the document body. Coming from the chip, moving the
     * focus into a field on the other side of the page would be a jump the user did not ask for.</p>
     */
    public clearNumberFilter(returnFocus = false): void {
        this.numberSearchControl.setValue('', { emitEvent: false });
        if (returnFocus) {
            this.numberSearchInput?.nativeElement.focus();
        }
        if (this.filter) {
            this.filter.displayNumber = '';
            this.filterChanged$.next();
        }
    }

    /**
     * Carries a number set in the filter panel over into the header field. `emitEvent: false` keeps the
     * write from starting a second search.
     *
     * <p>Compared on the trimmed value: the filter trims, the header field does not, and rewriting a
     * field the user is still typing in would take the blank away under their caret.</p>
     */
    private syncNumberSearchControl(): void {
        const current = this.filter?.displayNumber ?? '';
        if (this.numberSearchControl.value.trim() !== current) {
            this.numberSearchControl.setValue(current, { emitEvent: false });
        }
    }

    /**
     * Mirrors the active filter into the list URL so a filtered list can be shared and reloaded
     * (EPROC-11936). `replaceUrl` keeps the back button usable, and an empty filter clears the query
     * string - which also removes a consumed `?clean=true`.
     *
     * <p>The destroy guard is what keeps the start-up path honest: `getFilter()` awaits two lookups,
     * and leaving the list while they run would otherwise navigate the user back here - and, because
     * of `replaceUrl`, drop the page they actually chose out of the history.</p>
     */
    private applyFilterToUrl(): void {
        if (this.destroyed) {
            return;
        }
        void this.router.navigate([View.CLIENT, 'rfq', 'purchase-requisition', 'list'], {
            queryParams: this.filter?.filterAsParams ?? {},
            replaceUrl: true,
        });
    }

    public getData(): void {
        if (this.filter) {
            this.pagination.save();
            this.notificationService.spinner.show();
            this.purchaseRequisitionDataService
                .findAll(this.filter, this.pagination, this.sorting)
                .pipe(finalize(() => this.notificationService.spinner.hide()))
                .subscribe((response: IPaginationSortingResponse<PurchaseRequisitionPreview, any>) => {
                    this.purchaseRequisitions = response.data;
                    this.pagination.totalPages = response.pagination.totalPages;
                    this.pagination.totalEntries = response.pagination.totalEntries;
                });
        }
    }

    public openPdfUpload(): void {
        this.dialog
            .open<PurchaseRequisitionPdfUploadDialogComponent, undefined, PurchaseRequisitionPdfUploadDialogResult>(
                PurchaseRequisitionPdfUploadDialogComponent,
                DialogOptions.MEDIUM(),
            )
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((result) => {
                if (result?.created && result.demandId) {
                    this.unannouncedDemandExists = true;
                    void this.router.navigate([View.CLIENT, 'rfq', 'demand', result.demandId]);
                } else if (result?.ambiguous) {
                    this.openPdfSupplierSelection(result.ambiguous);
                }
            });
    }

    /**
     * The AI could not resolve the detected supplier unambiguously: let the user pick one of the
     * candidates (or search) next to the uploaded PDF, then navigate to the draft created with the
     * chosen supplier (EPROC-11092).
     */
    private openPdfSupplierSelection(ambiguous: PurchaseRequisitionPdfUploadAmbiguousResult): void {
        this.dialog
            .open<
                PdfSupplierSelectionDialogComponent,
                PdfSupplierSelectionDialogData,
                PdfSupplierSelectionDialogResult
            >(
                PdfSupplierSelectionDialogComponent,
                DialogOptions.LARGE(
                    new PdfSupplierSelectionDialogData(
                        ambiguous.file,
                        ambiguous.companyCodeId,
                        ambiguous.outcome.candidates,
                        ambiguous.outcome.detectedSupplierName,
                        ambiguous.outcome.detectedContactName,
                        ambiguous.outcome.detectedContactEmail,
                    ),
                ),
            )
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((result) => {
                if (result?.demandId) {
                    this.unannouncedDemandExists = true;
                    void this.router.navigate([View.CLIENT, 'rfq', 'demand', result.demandId]);
                }
            });
    }

    private async getFilter(): Promise<void> {
        const route = this.route.snapshot;
        const isClean = !!route.queryParams[PurchaseRequisitionListFilter.CLEAN_FILTER_KEY];
        const fromQueryParams = !isClean && PurchaseRequisitionListFilter.hasFilterInQueryParams(route);
        // Read before RESTORE: restoring from query params replaces the persisted filter, and the page
        // number only still belongs to the list if the incoming filter is the persisted one.
        const persistedFilterParams = fromQueryParams
            ? (await PurchaseRequisitionListFilter.RESTORE(this.storage)).filterAsParams
            : undefined;

        this.filter = await PurchaseRequisitionListFilter.RESTORE(this.storage, route);

        // Both lookups are independent of each other and of the filter - run them side by side so the
        // list start-up does not serialize two extra round-trips.
        await Promise.all([this.getClientOptions(), this.getDepartmentOptions()]);
        if (fromQueryParams) {
            await this.restoreObjectFiltersFromQueryParams(route.queryParams);
        }

        this.pagination = await this.restorePagination(isClean, persistedFilterParams);
        this.syncNumberSearchControl();
        if (!fromQueryParams) {
            // The URL already carries the filter on that path. Not rewriting it also keeps an object id
            // whose lookup just failed in the link, so a reload can still resolve it.
            this.applyFilterToUrl();
        }
        await this.paginationTranslations.translate(this.i18n);
    }

    /**
     * A shared link brings its own filter but the reader's stored page number; restoring both would
     * answer a link that has a hit with the empty state. The page therefore survives only when the link
     * shows the filter the stored page belongs to - which is the case on a plain reload, because the
     * URL always carries the active filter (EPROC-11936).
     */
    private async restorePagination(
        isClean: boolean,
        persistedFilterParams?: Params,
    ): Promise<PurchaseRequisitionPagination> {
        const filterCameFromElsewhere =
            persistedFilterParams !== undefined &&
            !PurchaseRequisitionListComponent.sameParams(persistedFilterParams, this.filter?.filterAsParams ?? {});

        if (isClean || filterCameFromElsewhere) {
            return PurchaseRequisitionPagination.default(this.storage, this.paginationService);
        }
        return PurchaseRequisitionPagination.restore(this.storage, this.paginationService);
    }

    private static sameParams(left: Params, right: Params): boolean {
        const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
        return [...keys].every((key) => left[key] === right[key]);
    }

    /**
     * The supplier, consumer and department filters hold resolved objects, so a shared link can only
     * carry their id and the option lists have to turn it back into an object. An id that is not in
     * the list any more (foreign tenant, deactivated supplier) drops out silently - a shared link is
     * the wrong place for an error message.
     */
    private async restoreObjectFiltersFromQueryParams(queryParams: Params): Promise<void> {
        const supplierId: string | undefined = queryParams[PurchaseRequisitionListFilterKey.SUPPLIER];
        const consumerId: string | undefined = queryParams[PurchaseRequisitionListFilterKey.CONSUMER];
        const departmentId: string | undefined = queryParams[PurchaseRequisitionListFilterKey.DEPARTMENT_ID];

        if (supplierId) {
            // The one filter whose option list this path has to wait for; the start-up itself does not.
            await this.getSupplierOptions();
            const supplier = this.suppliers?.find((candidate) => candidate.id === supplierId);
            if (supplier) {
                this.filter?.setSupplier(supplier);
            }
        }

        const consumer = consumerId ? this.clients?.find((candidate) => candidate.id === consumerId) : undefined;
        if (consumer) {
            this.filter?.setConsumer(consumer);
        }

        const department = departmentId
            ? this.departments?.find((candidate) => candidate.id === departmentId)
            : undefined;
        if (department) {
            this.filter?.setDepartment(department);
        }
    }

    /**
     * Loads the supplier filter options exactly once. The start-up fires this unawaited so the first
     * page does not wait for it, while a URL carrying `supplier=<id>` awaits the very same promise,
     * because that filter holds a resolved object and cannot be rebuilt from the id alone. Caching the
     * promise rather than its result keeps two concurrent callers from issuing two requests.
     *
     * <p>A failing lookup leaves `suppliers` undefined instead of assigning an empty array, so the
     * filter stays hidden rather than offering a supplier list with no suppliers in it - and, like the
     * other option lookups, it must not abort the page setup.</p>
     */
    private getSupplierOptions(): Promise<void> {
        this.supplierOptionsLoaded ??= firstValueFrom(this.supplierDataService.getAssignedSuppliers())
            .then((suppliers: SupplierCompany[]) => {
                this.suppliers = suppliers;
            })
            .catch(() => undefined);
        return this.supplierOptionsLoaded;
    }

    /**
     * Loads the option set of the "Bedarfsträger" filter from the visibility-derived lookup, so the
     * offered names are exactly those appearing in requisitions the user may see (EPROC-11749).
     *
     * The logged-in user is always part of the set: own involvement always grants access, so
     * filtering on oneself must be possible even before the first own requisition exists.
     *
     * <p>No automatic preselect of the logged-in user. Before EPROC-11749 that default was reachable
     * only inside the ACL gate `purchase_requisition.company`/`.department`; with the gate gone it
     * would apply to everyone, and since the creator/requester/approver predicates are OR'ed it would
     * hide requisitions the user may only see as an approver from their default list. Showing
     * everything visible and letting the user filter beats silently narrowing the list.</p>
     */
    private async getClientOptions(): Promise<void> {
        const selectable = await this.loadOptions(this.purchaseRequisitionDataService.findSelectableConsumers());
        const userIsSelectable = selectable.some((client) => client.equals(this.user.id));
        this.clients = userIsSelectable ? selectable : this.withCurrentUser(selectable);
    }

    private withCurrentUser(clients: PersonExtended[]): PersonExtended[] {
        return OrderByPipe.sortObjectsByStringValue([...clients, this.currentUserAsClient()], 'fullName');
    }

    /**
     * A failing lookup must not abort the page setup: without this the rejection would skip the
     * initial data request and the filter subscription, leaving an unusable list instead of a list
     * without filter options.
     */
    private async loadOptions<T>(options$: Observable<T[]>): Promise<T[]> {
        return firstValueFrom(options$.pipe(catchError(() => of([] as T[]))));
    }

    private currentUserAsClient(): PersonExtended {
        return selectableConsumerToPerson({
            id: this.user.id,
            firstName: this.user.firstName,
            surName: this.user.surName,
            title: '',
            active: true,
        });
    }

    /**
     * Loads the option set of the "Abteilung" filter from the visibility-derived lookup. The field
     * itself stays hidden while it offers no choice at all (see template).
     */
    private async getDepartmentOptions(): Promise<void> {
        this.departments = await this.loadOptions(this.purchaseRequisitionDataService.findSelectableDepartments());
    }

    private doesUnannouncedDemandExist(): void {
        this.demandDataService
            .hasUnannouncedDemand()
            .subscribe((hasUnannouncedDemand: boolean) => (this.unannouncedDemandExists = hasUnannouncedDemand));
    }
}
